import { Report } from 'api/types'
import { Icon } from 'components/Icon'
import { WORKFLOW_LABELS, formatDateTime } from 'lib/format'
import { t } from 'lib/i18n'

type StepState = 'reached' | 'current' | 'future' | 'returned'

interface Step {
  key: string
  label: string
  state: StepState
  actorName?: string | null
  at?: string | null
}

/** Report workflow stepper: Draft → Submitted → In review → Approved (or Returned). Figma node 133:638. */
export const ReportLifecycleTracker = ({ report }: { report: Report }) => {
  const { workflow } = report
  const isReturned = workflow === 'RETURNED'
  const isApproved = workflow === 'APPROVED'
  const isDraft = workflow === 'DRAFT' || workflow === 'EMPTY'

  const reviewState: StepState = isReturned ? 'returned' : isApproved ? 'reached' : isDraft ? 'future' : 'current'

  const steps: Step[] = [
    { actorName: report.preparedByName, key: 'draft', label: t('مسودة', 'Draft'), state: 'reached' },
    {
      actorName: report.submittedByName,
      at: report.submittedAt,
      key: 'submitted',
      label: t('مُرسل', 'Submitted'),
      state: isDraft ? 'future' : 'reached',
    },
    {
      actorName: isReturned ? report.returnedByName : null,
      at: isReturned ? report.returnedAt : null,
      key: 'review',
      label: isReturned ? t('تقرير للمراجعة', 'Report for review') : t('قيد المراجعة', 'In review'),
      state: reviewState,
    },
    {
      actorName: report.approvedByName,
      at: report.approvedAt,
      key: 'approved',
      label: t('معتمد', 'Approved'),
      state: isApproved ? 'reached' : 'future',
    },
  ]

  return (
    <div className="lifecycle" role="group" aria-label={`${t('حالة التقرير', 'Report status')}: ${WORKFLOW_LABELS[workflow]}`}>
      <ol className="lifecycle__steps">
        {steps.map((step, index) => (
          <li className={`lifecycle__step is-${step.state}`} key={step.key}>
            {index > 0 ? <span className="lifecycle__connector" aria-hidden="true" /> : null}
            <span className="lifecycle__dot" aria-hidden="true">
              {step.state === 'reached' ? (
                <Icon name="check" size={14} />
              ) : step.state === 'returned' ? (
                <Icon name="undo" size={14} />
              ) : step.state === 'current' ? (
                <Icon name="clock" size={14} />
              ) : (
                index + 1
              )}
            </span>
            <span className="lifecycle__label">{step.label}</span>
            {step.at || step.actorName ? (
              <span className="lifecycle__meta">
                {step.at ? formatDateTime(step.at) : null}
                {step.at && step.actorName ? ' · ' : null}
                {step.actorName || null}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      {isReturned && report.returnNote ? (
        <p className="lifecycle__reason" role="note">
          <Icon name="undo" size={14} /> {report.returnNote}
        </p>
      ) : null}
    </div>
  )
}
